import { logger } from '@/utils/logger/logger';
import {
  presentationAtom,
  presentationHistoryAtom,
  presentationOriginalAtom,
} from '@heidi/agents/data';
import { useQuery } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { fetchPresentation, fetchPresentationByChatId } from './presentationApi';

export const PRESENTATION_QUERY_KEY = 'presentation';
export const PRESENTATION_BY_CHAT_QUERY_KEY = 'presentation-by-chat';

export function usePresentation(presentationId: string | null) {
  const setPresentation = useSetAtom(presentationAtom);
  const setOriginal = useSetAtom(presentationOriginalAtom);
  const setHistory = useSetAtom(presentationHistoryAtom);

  const presentation = useAtomValue(presentationAtom);
  const hasPopulatedRef = useRef(false);

  // Safety valve: if something external (e.g. ChatWithCanvas chat-switch
  // effect) clears the atom after we already populated it, allow the next
  // API fetch to re-populate.
  useEffect(() => {
    if (!presentation && hasPopulatedRef.current) {
      hasPopulatedRef.current = false;
    }
  }, [presentation]);

  const query = useQuery({
    queryKey: [PRESENTATION_QUERY_KEY, presentationId],
    queryFn: () => fetchPresentation(presentationId!),
    enabled: !!presentationId,
    staleTime: 0,
    retry: 1,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!query.data) return;

    // Wait for the fetch to complete -- don't apply stale cache while a
    // background refetch is in-flight (prevents latest → old → latest flash).
    if (query.fetchStatus === 'fetching') return;

    // Already populated during this mount cycle (e.g. mutation-triggered
    // refetch). Skip so we don't overwrite local edits or wipe undo history.
    if (hasPopulatedRef.current) return;

    logger.info('[SlideBuilder] API fetch succeeded — populating atoms', {
      presentationId: query.data.id,
      slideCount: query.data.slides.length,
      title: query.data.title,
    });

    hasPopulatedRef.current = true;
    setPresentation(query.data);
    setOriginal(query.data);
    setHistory({ past: [], future: [] });
  }, [query.data, query.fetchStatus, setPresentation, setOriginal, setHistory]);

  // Reset when switching to a different presentation
  useEffect(() => {
    hasPopulatedRef.current = false;
  }, [presentationId]);

  useEffect(() => {
    if (query.error) {
      logger.warn('[SlideBuilder] API fetch failed', {
        presentationId,
        error: query.error,
      });
    }
  }, [query.error, presentationId]);

  return query;
}

/**
 * Lightweight query that resolves the presentation ID linked to a chat.
 * Returns only the ID (not the full data) — used by ChatWithCanvas to
 * decide whether to auto-open the PresentationPanel.
 */
export function usePresentationIdForChat(chatId: string | null | undefined) {
  return useQuery({
    queryKey: [PRESENTATION_BY_CHAT_QUERY_KEY, chatId],
    queryFn: async () => {
      const data = await fetchPresentationByChatId(chatId!);
      return data.id;
    },
    enabled: !!chatId,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
  });
}
